import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Tabs } from "@/components/ui/tabs";
import { AuctionsFilterTabs } from "@/components/auctions/AuctionsFilterTabs";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { Button } from "@/components/ui/button";
import { Car, Home } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { Pagination } from "@/components/ui/pagination";
import { useCampaigns, usePublicAuctionCampaigns } from "@/hooks/useCampaigns";
import { GuestLoginPrompt } from "@/components/auth/GuestLoginPrompt";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useListing,
  getOrCreateForYouSeed,
  rotateForYouSeed,
} from "@/hooks/useListing";
import { FilterBar } from "@/components/auctions/FilterBar";
import CarCard from "@/components/auctions/CarCard";
import {
  AuctionsBrowseTabs,
  type AuctionsBrowseMode,
} from "@/components/auctions/AuctionsBrowseTabs";
import { ForYouFeed } from "@/components/auctions/for-you/ForYouFeed";
import { ForYouFeedSkeleton } from "@/components/auctions/for-you/ForYouFeedSkeleton";
import { useAllaToForYouSwipe } from "@/components/auctions/for-you/useAllaToForYouSwipe";
import {
  filterAndSortListings,
  FilterState,
  listingsEndingSoonRefetchInterval,
  mapListingSortToApi,
} from "@/utils/listingUtils";
import { ListingOverview } from "@/types/listing";
import { useFavorites } from "@/hooks/useFavorites";
import { toast } from "sonner";
import { auctionsLabels } from "@/i18n/labels/auctionsLabels";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n/labels/commonLabels";

import { cn } from "@/lib/utils";
import { normalizePath } from "@/lib/native/nativeRoutes";
import { NativeCampaignCarousel } from "@/components/native/NativeCampaignCarousel";
import { useNativeListingsSearchSort } from "@/components/native/useNativeListingsSearchSort";
import { NativeListingsSkeleton } from "@/components/native/NativeListingsSkeleton";
import VehicleListingsGrid from "@/components/index/VehicleListingsGrid";
import { navigateToListingFromClick } from "@/lib/native/listingNavigation";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import {
  applyListingsScrollRestore,
  consumeListingsScrollRestore,
  saveListingsScrollY,
} from "@/lib/native/listingsScrollRestore";
import { useShowNavigateBack } from "@/hooks/useShowNavigateBack";
import { resetAuthRefreshState } from "@/integrations/clients/carAuction/carAuctionClient";
import {
  AuctionsGuestHeaderHome,
  AuctionsGuestHeaderLogin,
} from "@/components/auctions/AuctionsGuestHeaderNav";

const VIEW_MODE_STORAGE_KEY = "campaignDetails_viewMode";
const BROWSE_MODE_STORAGE_KEY = "auctions_browseMode";

const Auctions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { language } = useLanguage();
  const labels = auctionsLabels(language);
  const [filterBy, setFilterBy] = useState("all");
  const { user } = useAuth();
  const isGuest = !user;
  const [guestLoginOpen, setGuestLoginOpen] = useState(false);
  const isMobile = useIsMobile();
  const { isNative: isNativeApp } = useNativePlatform();
  const showPageBack = useShowNavigateBack();
  // Native app: hub at /auctions, full browse at /auctions/all.
  // Mobile web keeps the browse shell (sticky header + home + Alla/För dig).
  const isAuctionsHub =
    isNativeApp && normalizePath(location.pathname) === "/auctions";
  const isNativeAllListings =
    isNativeApp && normalizePath(location.pathname) === "/auctions/all";
  const listingsSearchRef = useRef<HTMLDivElement>(null);

  const getParamInt = useCallback(
    (key: string, fallback: number) => {
      const raw = searchParams.get(key);
      const val = raw != null ? Number(raw) : NaN;
      return Number.isFinite(val) && val > 0 ? val : fallback;
    },
    [searchParams],
  );

  const getParamStr = useCallback(
    (key: string, fallback = "") => {
      return searchParams.get(key) ?? fallback;
    },
    [searchParams],
  );

  // Load viewMode from localStorage, default to "narrow"
  const [viewMode, setViewMode] = useState<"wide" | "narrow" | "grid">(() => {
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return saved === "wide" || saved === "narrow" || saved === "grid"
      ? saved
      : "narrow";
  });

  // Save viewMode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  const [browseMode, setBrowseMode] = useState<AuctionsBrowseMode>(() => {
    const fromUrl = searchParams.get("browse");
    if (fromUrl === "foryou" || fromUrl === "all") return fromUrl;
    const saved = localStorage.getItem(BROWSE_MODE_STORAGE_KEY);
    return saved === "foryou" ? "foryou" : "all";
  });

  useEffect(() => {
    localStorage.setItem(BROWSE_MODE_STORAGE_KEY, browseMode);
  }, [browseMode]);

  const handleBrowseModeChange = useCallback(
    (mode: AuctionsBrowseMode) => {
      setBrowseMode(mode);
      const next = new URLSearchParams(searchParams);
      if (mode === "foryou") next.set("browse", "foryou");
      else next.delete("browse");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const {
    targetRef: browseSwipeRef,
    progress: forYouDragProgress,
    settling: forYouDragSettling,
    allStyle: allaSwipeStyle,
    showIncoming: showForYouIncoming,
    animateToForYou,
  } = useAllaToForYouSwipe({
    enabled: browseMode === "all",
    onCommitForYou: () => handleBrowseModeChange("foryou"),
  });

  const handleBrowseModeChangeAnimated = useCallback(
    (mode: AuctionsBrowseMode) => {
      if (mode === "foryou" && browseMode === "all") {
        animateToForYou();
        return;
      }
      handleBrowseModeChange(mode);
    },
    [animateToForYou, browseMode, handleBrowseModeChange],
  );

  const showForYouFeed = browseMode === "foryou" || showForYouIncoming;
  const forYouPreview = browseMode !== "foryou" && showForYouIncoming;

  // Fetch campaigns via authenticated GET /campaigns (filtered by role; admins see all)
  const {
    campaigns: authCampaigns,
    isLoadingCampaigns: isLoadingAuthCampaigns,
    campaignsError: authCampaignsError,
    refetchCampaigns: refetchAuthCampaigns,
  } = useCampaigns(1, 100, { enabled: !isGuest });
  const {
    publicAuctionCampaigns,
    isLoadingPublicAuctionCampaigns,
    publicAuctionCampaignsError,
    refetchPublicAuctionCampaigns,
  } = usePublicAuctionCampaigns(100);

  const campaignsForAuctions = useMemo(() => {
    if (isGuest) {
      return (publicAuctionCampaigns || []).map((c) => ({
        _id: c.id,
        id: c.id,
        name: c.name,
        description: c.description,
        status: c.status,
        type: c.type,
        totalListings: c.totalListings,
        thumbnailUrl: c.thumbnailUrl,
        thumbnailImage: c.thumbnailUrl ? { url: c.thumbnailUrl } : undefined,
        labels: c.labels ?? [],
        endDate: (c as { endDate?: string }).endDate,
        targetUserType: (c as { targetUserType?: string }).targetUserType,
      }));
    }
    return (authCampaigns || []).filter(
      (c) => c.status === "active" || c.status === "upcoming",
    );
  }, [isGuest, publicAuctionCampaigns, authCampaigns]);

  const isLoadingCampaigns = isGuest
    ? isLoadingPublicAuctionCampaigns
    : isLoadingAuthCampaigns;
  const campaignsError = isGuest
    ? publicAuctionCampaignsError
    : authCampaignsError;
  const refetchCampaigns = isGuest
    ? refetchPublicAuctionCampaigns
    : refetchAuthCampaigns;

  // Server-side pagination state for listings
  const [listingsPage, setListingsPage] = useState(() =>
    getParamInt("page", 1),
  );
  const [listingsItemsPerPage, setListingsItemsPerPage] = useState(() =>
    getParamInt("limit", 50),
  );

  // Search state - separate input value from applied search
  const [searchTerm, setSearchTerm] = useState(() => getParamStr("q", "")); // Input value
  const [appliedSearch, setAppliedSearch] = useState(() =>
    getParamStr("q", ""),
  ); // Search term sent to backend

  // Filter and sort state (sortBy must be declared before queryParams useMemo)
  const [sortBy, setSortBy] = useState(() =>
    getParamStr("sort", "createdAt-desc"),
  );
  const [filters, setFilters] = useState<FilterState>(() => ({
    make: getParamStr("make", ""),
    model: getParamStr("model", ""),
    minMileage: getParamStr("minMileage", ""),
    maxMileage: getParamStr("maxMileage", ""),
    minYear: getParamStr("minYear", ""),
    maxYear: getParamStr("maxYear", ""),
    transmission: getParamStr("transmission", ""),
    fuelType: getParamStr("fuelType", ""),
    minPower: getParamStr("minPower", ""),
    maxPower: getParamStr("maxPower", ""),
    bodyType: getParamStr("bodyType", ""),
    minPrice: getParamStr("minPrice", ""),
    maxPrice: getParamStr("maxPrice", ""),
  }));

  // Keep URL query in sync so browser back preserves state
  useEffect(() => {
    const next = new URLSearchParams();

    if (browseMode === "foryou") next.set("browse", "foryou");
    if (appliedSearch) next.set("q", appliedSearch);
    if (sortBy && sortBy !== "createdAt-desc") next.set("sort", sortBy);
    if (listingsPage !== 1) next.set("page", String(listingsPage));
    if (listingsItemsPerPage !== 50)
      next.set("limit", String(listingsItemsPerPage));

    (Object.entries(filters) as Array<[keyof FilterState, string]>).forEach(
      ([k, v]) => {
        if (v) next.set(String(k), v);
      },
    );

    // Avoid replacing when identical (prevents loops)
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    browseMode,
    appliedSearch,
    sortBy,
    listingsPage,
    listingsItemsPerPage,
    filters,
    searchParams,
    setSearchParams,
  ]);

  // Fetch listings with server-side pagination
  // Backend now supports multiple statuses via comma-separated string or array
  const {
    getListingsWithFilters,
    getPublicListingsWithFilters,
    getForYouListingsInfinite,
    getPublicForYouListingsInfinite,
  } = useListing();

  // Alla-tabben: only active (no reserved) — ordinary paginated browse.
  const queryParams = useMemo(
    () => ({
      page: listingsPage,
      limit: listingsItemsPerPage,
      status: ["active"] as ("active")[],
      search: appliedSearch || undefined,
      ...(mapListingSortToApi(sortBy)
        ? { sort: mapListingSortToApi(sortBy) }
        : {}),
    }),
    [listingsPage, listingsItemsPerPage, appliedSearch, sortBy],
  );

  const [forYouSeed, setForYouSeed] = useState(() => getOrCreateForYouSeed());
  const forYouBaseParams = useMemo(
    () => ({
      status: ["active", "reserved"] as ("active" | "reserved")[],
      seed: forYouSeed,
    }),
    [forYouSeed],
  );

  // Soft-poll when any auction on this page ends within 5 min (anti-sniping endTime updates).
  const endingSoonPoll = useCallback(
    (query: { state: { data?: { listings?: ListingOverview[] } } }) =>
      listingsEndingSoonRefetchInterval(query.state.data?.listings),
    [],
  );

  const authListingsQuery = getListingsWithFilters(queryParams, {
    enabled: !isGuest && browseMode !== "foryou",
    refetchInterval: endingSoonPoll,
    refetchIntervalInBackground: false,
  });
  const publicListingsQuery = getPublicListingsWithFilters(queryParams, {
    enabled: isGuest && browseMode !== "foryou",
    refetchInterval: endingSoonPoll,
    refetchIntervalInBackground: false,
  });

  // För dig: infinite pages, seeded random (no re-shows within the seed).
  // Prefetch on native hub / all-listings so first swipe isn't a blank "Laddar…"
  const forYouEnabled =
    showForYouFeed || isAuctionsHub || isNativeAllListings;
  const authForYouQuery = getForYouListingsInfinite(forYouBaseParams, {
    enabled: !isGuest && forYouEnabled,
  });
  const publicForYouQuery = getPublicForYouListingsInfinite(forYouBaseParams, {
    enabled: isGuest && forYouEnabled,
  });
  const forYouQuery = isGuest ? publicForYouQuery : authForYouQuery;

  const listingsData = isGuest
    ? publicListingsQuery.data
    : authListingsQuery.data;
  const isLoadingListings = isGuest
    ? publicListingsQuery.isLoading
    : authListingsQuery.isLoading;
  const listingsError = isGuest
    ? publicListingsQuery.error
    : authListingsQuery.error;
  const refetchListings = isGuest
    ? publicListingsQuery.refetch
    : authListingsQuery.refetch;

  const handleRetryFetch = useCallback(() => {
    // Clear soft refresh-block from transient auth failures so retry can succeed.
    resetAuthRefreshState();
    void refetchCampaigns();
    void refetchListings();
    void forYouQuery.refetch();
  }, [refetchCampaigns, refetchListings, forYouQuery]);

  useEffect(() => {
    if (isLoadingListings) return;
    const stateY = (location.state as { restoreScrollY?: number } | null)
      ?.restoreScrollY;
    const y = consumeListingsScrollRestore(
      location.pathname,
      location.search,
      stateY,
    );
    if (y == null) return;
    return applyListingsScrollRestore(y);
  }, [isLoadingListings, location.pathname, location.search, location.state]);

  // Use listings directly from backend (already paginated server-side)
  const allListings = useMemo(() => {
    return listingsData?.listings || [];
  }, [listingsData?.listings]);

  // Filter listings based on campaign targetUserType and user role
  // Also exclude listings from upcoming campaigns
  const filteredListings = useMemo(() => {
    if (!allListings.length) return [];

    if (isGuest) {
      return allListings;
    }

    // Get user's allowed campaign targetUserTypes
    const userType = user?.type;
    const allowedTargetUserTypes: string[] = [];

    if (userType === UserType.Dealer) {
      allowedTargetUserTypes.push("all", "dealers-only");
    } else if (
      userType === UserType.Individual ||
      userType === UserType.Business
    ) {
      allowedTargetUserTypes.push("all", "individual-and-business-only");
    } else if (userType === UserType.Admin) {
      allowedTargetUserTypes.push(
        "all",
        "dealers-only",
        "individual-and-business-only",
      );
    } else {
      // Not logged in - only see 'all'
      allowedTargetUserTypes.push("all");
    }

    const campaignMap = new Map<
      string,
      { status: string; targetUserType: string }
    >();
    campaignsForAuctions.forEach((c) => {
      campaignMap.set(c._id, {
        status: c.status,
        targetUserType: c.targetUserType,
      });
    });

    return allListings.filter((listing) => {
      if (listing.status !== "active") return false;
      if (!listing.campaignId) return true;

      const campaignInfo = campaignMap.get(listing.campaignId);
      if (!campaignInfo) {
        if (!user) return false;
        return true;
      }
      if (campaignInfo.status === "upcoming") return false;
      return allowedTargetUserTypes.includes(campaignInfo.targetUserType);
    });
  }, [allListings, campaignsForAuctions, user, isGuest]);

  // Apply filters and sorting to listings (client-side for filters, search is server-side)
  const filteredAndSortedListings = useMemo(() => {
    return filterAndSortListings(
      filteredListings,
      "", // No client-side search - search is handled by backend
      sortBy,
      filters,
    );
  }, [filteredListings, sortBy, filters]);

  // For server-side pagination, we use the listings directly from backend
  // Note: Search and filters are applied client-side after server-side pagination
  // This means filters/search work on the current page only, not across all listings
  const paginatedListings = filteredAndSortedListings;

  // Use backend totalCount for pagination and for "X fordon" (total antal annonser)
  const totalListingsCount = listingsData?.totalCount || 0;

  // Calculate total pages based on backend total count
  const totalListingsPages = Math.ceil(
    totalListingsCount / listingsItemsPerPage,
  );

  // Handle page change (no scroll – stay in place)
  const handleListingsPageChange = useCallback((page: number) => {
    setListingsPage(page);
  }, []);

  // Handle items per page change
  const handleListingsItemsPerPageChange = useCallback(
    (newItemsPerPage: number) => {
      setListingsItemsPerPage(newItemsPerPage);
      setListingsPage(1); // Reset to first page
    },
    [],
  );

  // Handle search - apply search to backend
  const handleSearch = useCallback(() => {
    setAppliedSearch(searchTerm);
    setListingsPage(1); // Reset to first page when searching
  }, [searchTerm]);

  // Handle clear search - reset search and show original list
  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setAppliedSearch("");
    setListingsPage(1); // Reset to first page
  }, []);

  const [nativeFilterBarVisible, setNativeFilterBarVisible] = useState(true);
  const nativeListingsSearchSort = useNativeListingsSearchSort({
    sortBy,
    onSortChange: setSortBy,
  });

  const clearFilters = () => {
    setFilters({
      make: "",
      model: "",
      minMileage: "",
      maxMileage: "",
      minYear: "",
      maxYear: "",
      transmission: "",
      fuelType: "",
      minPower: "",
      maxPower: "",
      bodyType: "",
      minPrice: "",
      maxPrice: "",
    });
    setSearchTerm("");
    setAppliedSearch(""); // Clear backend search too
    setListingsPage(1); // Reset to first page
  };

  // Favorites handling
  const userId = user?.id || "user1";
  const { addFavorite, removeFavorite } = useFavorites(userId);
  const [favoriteChanges, setFavoriteChanges] = useState<
    Record<string, boolean>
  >({});

  const handleAddFavorite = useCallback(
    async (listingId: string) => {
      if (isGuest) {
        setGuestLoginOpen(true);
        return;
      }
      const { hapticFavoriteToggle } =
        await import("@/lib/native/hapticFeedback");
      hapticFavoriteToggle();
      setFavoriteChanges((prev) => ({ ...prev, [listingId]: true }));
      try {
        const data = await addFavorite({ listingId, userId });
        toast.success(labels.favorites.addSuccess);
      } catch (error) {
        setFavoriteChanges((prev) => {
          const newState = { ...prev };
          delete newState[listingId];
          return newState;
        });
        toast.error(labels.favorites.addError);
      }
    },
    [addFavorite, userId, isGuest],
  );

  const handleRemoveFavorite = useCallback(
    async (listingId: string) => {
      if (isGuest) {
        setGuestLoginOpen(true);
        return;
      }
      const { hapticFavoriteToggle } =
        await import("@/lib/native/hapticFeedback");
      hapticFavoriteToggle();
      setFavoriteChanges((prev) => ({ ...prev, [listingId]: false }));
      try {
        const data = await removeFavorite({ listingId, userId });
        toast.success(labels.favorites.removeSuccess);
      } catch (error) {
        setFavoriteChanges((prev) => {
          const newState = { ...prev };
          delete newState[listingId];
          return newState;
        });
        toast.error(labels.favorites.removeError);
      }
    },
    [removeFavorite, userId, labels],
  );

  const applyFavoriteOverrides = useCallback(
    (items: ListingOverview[]) =>
      items.map((listing) => ({
        ...listing,
        isFavorite: Object.prototype.hasOwnProperty.call(
          favoriteChanges,
          listing.id,
        )
          ? favoriteChanges[listing.id]
          : listing.isFavorite,
      })),
    [favoriteChanges],
  );

  // Apply favorite changes to paginated listings
  const listingsWithFavorites = useMemo(
    () => applyFavoriteOverrides(paginatedListings),
    [paginatedListings, applyFavoriteOverrides],
  );

  /** För dig: flatten infinite pages (seeded random; client dedupe as safety). */
  const forYouRawListings = useMemo(() => {
    const pages = forYouQuery.data?.pages ?? [];
    const seen = new Set<string>();
    const flat: ListingOverview[] = [];
    for (const page of pages) {
      for (const item of page.listings || []) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        flat.push(item);
      }
    }
    return flat;
  }, [forYouQuery.data?.pages]);

  // Same campaign visibility rules as Alla (upcoming / targetUserType).
  const forYouFilteredListings = useMemo(() => {
    if (!forYouRawListings.length) return [];
    if (isGuest) return forYouRawListings;

    const userType = user?.type;
    const allowedTargetUserTypes: string[] = [];
    if (userType === UserType.Dealer) {
      allowedTargetUserTypes.push("all", "dealers-only");
    } else if (
      userType === UserType.Individual ||
      userType === UserType.Business
    ) {
      allowedTargetUserTypes.push("all", "individual-and-business-only");
    } else if (userType === UserType.Admin) {
      allowedTargetUserTypes.push(
        "all",
        "dealers-only",
        "individual-and-business-only",
      );
    } else {
      allowedTargetUserTypes.push("all");
    }

    const campaignMap = new Map<
      string,
      { status: string; targetUserType: string }
    >();
    campaignsForAuctions.forEach((c) => {
      campaignMap.set(c._id, {
        status: c.status,
        targetUserType: c.targetUserType,
      });
    });

    return forYouRawListings.filter((listing) => {
      if (listing.status !== "active" && listing.status !== "reserved")
        return false;
      if (!listing.campaignId) return true;
      const campaignInfo = campaignMap.get(listing.campaignId);
      if (!campaignInfo) return Boolean(user);
      if (campaignInfo.status === "upcoming") return false;
      return allowedTargetUserTypes.includes(campaignInfo.targetUserType);
    });
  }, [forYouRawListings, campaignsForAuctions, user, isGuest]);

  const forYouListings = useMemo(
    () => applyFavoriteOverrides(forYouFilteredListings),
    [forYouFilteredListings, applyFavoriteOverrides],
  );

  const forYouHasMore = Boolean(forYouQuery.hasNextPage);
  const forYouFetchingMore = forYouQuery.isFetchingNextPage;
  const handleForYouLoadMore = useCallback(() => {
    if (!forYouQuery.hasNextPage || forYouQuery.isFetchingNextPage) return;
    void forYouQuery.fetchNextPage();
  }, [forYouQuery]);
  const handleForYouRefresh = useCallback(async () => {
    // New seed → new shuffle order; queryKey change reloads from page 1.
    setForYouSeed(rotateForYouSeed());
  }, []);

  const campaignCards = useMemo(() => {
    return campaignsForAuctions.map((c) => ({
      id: c._id,
      title: c.name,
      description: c.description,
      totalCars: c.totalListings ?? 0,
      type: c.type,
      image:
        c.thumbnailImage?.url ??
        ("thumbnailUrl" in c
          ? (c as { thumbnailUrl?: string }).thumbnailUrl
          : "") ??
        "",
      status: (c.status ?? "active") as
        | "active"
        | "upcoming"
        | "completed"
        | "cancelled",
      isAuction: c.type === "auction",
      targetUserType: c.targetUserType,
      labels: c.labels ?? [],
      date: c.endDate,
    }));
  }, [campaignsForAuctions]);

  const handleHubListingClick = useCallback(
    (listing: ListingOverview, e?: React.MouseEvent) => {
      if (e) {
        try {
          saveListingsScrollY(
            location.pathname,
            location.search,
            window.scrollY,
          );
        } catch {
          // ignore
        }
        void navigateToListingFromClick(navigate, listing, e, {
          from: "/auctions",
          fromScrollY: window.scrollY,
        });
      }
    },
    [navigate, location.pathname, location.search],
  );

  const nativeCampaignSlides = useMemo(
    () =>
      campaignCards.map((c) => ({
        id: c.id,
        title: c.title,
        description: c.description,
        image: c.image,
        totalCars: c.totalCars,
        isAuction: c.isAuction,
        status: c.status,
        labels: c.labels,
      })),
    [campaignCards],
  );


  if (isLoadingCampaigns) {
    return (
      <div className="min-h-screen bg-transparent relative">
        <div className="container max-w-6xl py-2 px-0.5 sm:px-4 relative z-10">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg">{labels.loading.listings}</div>
          </div>
        </div>
      </div>
    );
  }

  if (campaignsError) {
    return (
      <div className="min-h-screen bg-transparent relative">
        <div className="container max-w-6xl py-2 px-0.5 sm:px-4 relative z-10">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-red-500">
              {labels.error.fetchListings}
              <Button onClick={handleRetryFetch} className="ml-4">
                {labels.actions.tryAgain}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isAuctionsHub) {
    return (
      <div
        ref={browseSwipeRef}
        className="native-auctions-hub min-h-screen bg-transparent relative w-full min-w-full self-stretch overflow-x-hidden pb-4"
      >
        {showForYouFeed &&
          (forYouQuery.isLoading && forYouListings.length === 0 ? (
            <ForYouFeedSkeleton />
          ) : (
            <ForYouFeed
              listings={forYouListings}
              browseMode="foryou"
              onBrowseModeChange={handleBrowseModeChange}
              resumeScope="hub"
              preview={forYouPreview}
              dragProgress={forYouPreview ? forYouDragProgress : undefined}
              dragSettling={forYouPreview ? forYouDragSettling : false}
              hasMore={forYouHasMore}
              isFetchingMore={forYouFetchingMore}
              onLoadMore={handleForYouLoadMore}
              onRefresh={handleForYouRefresh}
              onFavoriteToggle={(listingId) => {
                const listing = forYouListings.find((l) => l.id === listingId);
                if (!listing) return;
                if (listing.isFavorite) handleRemoveFavorite(listingId);
                else handleAddFavorite(listingId);
              }}
            />
          ))}

        {browseMode !== "foryou" && (
          <div
            className="container max-w-6xl px-3 relative z-10"
            style={allaSwipeStyle}
          >
            <div className="mb-4 grid grid-cols-[auto_1fr_auto] items-center gap-2 pt-1">
              <div className="justify-self-start">
                {!isGuest ? (
                  <BtnNavigateBack
                    label={
                      commonLabels(language).menu?.dashboard ?? "Dashboard"
                    }
                    to="/dashboard"
                    iconOnly
                    icon={Home}
                    className="mb-0 h-9 w-9 rounded-xl px-0 justify-center text-sm font-medium border-0 text-foreground/90 bg-black/[0.05] ring-1 ring-black/10 hover:bg-black/[0.08]"
                  />
                ) : (
                  <AuctionsGuestHeaderHome compact />
                )}
              </div>
              <AuctionsBrowseTabs
                mode={browseMode}
                onModeChange={handleBrowseModeChangeAnimated}
                allLabel={labels.browse.allCars}
                forYouLabel={labels.browse.forYou}
                variant="onLight"
              />
              <div className="justify-self-end">
                {isGuest ? (
                  <AuctionsGuestHeaderLogin compact />
                ) : (
                  <span className="inline-block h-9 w-9" aria-hidden />
                )}
              </div>
            </div>
            <div className="space-y-6">
              <NativeCampaignCarousel
                campaigns={nativeCampaignSlides}
                isAuthenticated={!isGuest}
              />

              <Button
                type="button"
                size="lg"
                className="native-all-listings-cta w-full rounded-2xl h-12 text-[1.0625rem] font-semibold gap-3"
                onClick={() => navigate("/auctions/all")}
              >
                <Car className="h-5 w-5 shrink-0" strokeWidth={2.25} />
                {labels.titles.allListings ?? "Alla annonser"}
              </Button>

              <VehicleListingsGrid onCarClick={handleHubListingClick} />
            </div>
          </div>
        )}
        <GuestLoginPrompt
          open={guestLoginOpen}
          onOpenChange={setGuestLoginOpen}
        />
      </div>
    );
  }

  return (
    <div
      ref={browseSwipeRef}
      className={cn(
        "min-h-screen bg-transparent relative w-full min-w-full self-stretch overflow-x-hidden",
        isNativeApp && "native-auctions-page",
        isNativeAllListings && "native-all-listings-page",
      )}
    >
      {/* Gallery-style För dig pane — real content slides in with the finger */}
      {showForYouFeed &&
        (forYouQuery.isLoading && forYouListings.length === 0 ? (
          <ForYouFeedSkeleton />
        ) : (
          <ForYouFeed
            listings={forYouListings}
            browseMode="foryou"
            onBrowseModeChange={handleBrowseModeChange}
            resumeScope="all"
            preview={forYouPreview}
            dragProgress={forYouPreview ? forYouDragProgress : undefined}
            dragSettling={forYouPreview ? forYouDragSettling : false}
            hasMore={forYouHasMore}
            isFetchingMore={forYouFetchingMore}
            onLoadMore={handleForYouLoadMore}
            onRefresh={handleForYouRefresh}
            onFavoriteToggle={(listingId) => {
              const listing = forYouListings.find((l) => l.id === listingId);
              if (!listing) return;
              if (listing.isFavorite) handleRemoveFavorite(listingId);
              else handleAddFavorite(listingId);
            }}
          />
        ))}

      {/* Fixed browse header — outside swipe transform (sticky breaks under translate3d) */}
      {browseMode !== "foryou" && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-40">
          {/* Fade only behind tabs — keep short so FilterBar/search stay sharp in front */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-[4.75rem]"
            style={{
              background:
                "linear-gradient(to bottom, hsl(var(--background)) 0%, hsl(var(--background) / 0.97) 70%, hsl(var(--background) / 0) 100%)",
            }}
          />
          <div
            className="pointer-events-auto relative mx-auto w-full max-w-6xl px-2.5 sm:px-4"
            style={{
              paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)",
              paddingBottom: "0.5rem",
            }}
          >
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2">
              <div className="relative z-20 justify-self-start">
                {!isGuest ? (
                  <BtnNavigateBack
                    label={
                      commonLabels(language).menu?.dashboard ?? "Dashboard"
                    }
                    to="/dashboard"
                    iconOnly={isMobile || isNativeApp}
                    icon={isMobile || isNativeApp ? Home : undefined}
                    className={cn(
                      "mb-0 h-9 rounded-xl text-sm font-medium border-0",
                      isMobile || isNativeApp
                        ? "w-9 px-0 justify-center text-foreground/90 bg-black/[0.05] ring-1 ring-black/10 hover:bg-black/[0.08]"
                        : "px-2.5",
                    )}
                  />
                ) : (
                  <AuctionsGuestHeaderHome
                    compact={isMobile || isNativeApp}
                  />
                )}
              </div>
              <AuctionsBrowseTabs
                mode={browseMode}
                onModeChange={handleBrowseModeChangeAnimated}
                allLabel={labels.browse.allCars}
                forYouLabel={labels.browse.forYou}
                variant="onLight"
              />
              <div className="relative z-20 justify-self-end">
                {isGuest ? (
                  <AuctionsGuestHeaderLogin
                    compact={isMobile || isNativeApp}
                  />
                ) : (
                  <span
                    className="invisible pointer-events-none"
                    aria-hidden
                  >
                    {isMobile || isNativeApp ? (
                      <span className="inline-flex h-9 w-9" />
                    ) : (
                      <span className="inline-flex h-9 items-center px-2.5 text-sm font-medium">
                        {commonLabels(language).menu?.dashboard ?? "Dashboard"}
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        // No z-index here — FilterBar pins with its own z and must stack above the header fade
        className="container max-w-6xl px-0.5 sm:px-4 relative"
        style={browseMode === "all" ? allaSwipeStyle : undefined}
      >
        {/* Spacer so content clears the fixed header */}
        {browseMode !== "foryou" && (
          <div
            className="shrink-0"
            style={{
              height: "calc(env(safe-area-inset-top, 0px) + 3.75rem)",
            }}
            aria-hidden
          />
        )}

        <div className="native-page-back">
          {showPageBack && isNativeApp && !isNativeAllListings && (
            <BtnNavigateBack
              label={isGuest ? labels.campaignDetails.back : undefined}
              to={isGuest ? "/auctions" : "/dashboard"}
            />
          )}
          {isNativeAllListings && (
            <BtnNavigateBack
              label={commonLabels(language).buttons?.back ?? "Tillbaka"}
              to="/auctions"
            />
          )}
        </div>

        {/* Campaign gallery — same carousel as native hub, on web mobile + desktop */}
        {!isNativeAllListings && browseMode === "all" && (
          <div className="mb-2">
            <NativeCampaignCarousel
              campaigns={
                filterBy === "all"
                  ? nativeCampaignSlides
                  : filterBy === "auction"
                    ? nativeCampaignSlides.filter((c) => c.isAuction)
                    : nativeCampaignSlides.filter((c) => !c.isAuction)
              }
              isAuthenticated={!isGuest}
              variant="web"
              filters={
                <Tabs value={filterBy} onValueChange={setFilterBy}>
                  <AuctionsFilterTabs />
                </Tabs>
              }
            />
          </div>
        )}

        {/* Listings Section */}
        <div
          className={cn(
            "mb-8",
            browseMode === "all" && !isNativeAllListings && "mt-8",
            isNativeAllListings && "mt-4",
            browseMode === "foryou" && "mt-2",
          )}
        >
          {browseMode === "all" && !isNativeAllListings && (
          <div className="flex items-center justify-center mb-4">
            <h2 className="text-xl md:text-2xl font-bold">
              {labels.titles.allListings}
            </h2>
          </div>
          )}

          {browseMode === "foryou" ? (
            // Feed is portaled above; keep a spacer so layout doesn't jump
            <div className="h-0" aria-hidden />
          ) : isLoadingListings ? (
            isNativeAllListings ? (
              <NativeListingsSkeleton count={8} />
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="text-lg">{labels.loading.listings}</div>
              </div>
            )
          ) : listingsError ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-lg text-red-500">
                {labels.error.fetchListings}
                <Button onClick={handleRetryFetch} className="ml-4">
                  {labels.actions.tryAgain}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div
                ref={listingsSearchRef}
                className={cn(isNativeApp && "mb-2")}
              >
                <FilterBar
                  searchTerm={searchTerm}
                  sortBy={sortBy}
                  filters={filters}
                  onSearchChange={setSearchTerm}
                  onSearch={handleSearch}
                  onSortChange={setSortBy}
                  onFiltersChange={setFilters}
                  onClearFilters={clearFilters}
                  totalCars={totalListingsCount}
                  viewMode={viewMode}
                  onViewModeChange={(mode) => setViewMode(mode)}
                  showViewToggle
                  hideSortControls={isNativeAllListings}
                  nativeInlineControls={
                    isNativeAllListings
                      ? nativeListingsSearchSort.inlineSortButton()
                      : undefined
                  }
                  hasActiveSearch={!!appliedSearch}
                  onClearSearch={handleClearSearch}
                  onNativeBarVisibilityChange={
                    isNativeAllListings ? setNativeFilterBarVisible : undefined
                  }
                />
              </div>

              {filteredAndSortedListings.length === 0 && !isLoadingListings ? (
                <div className="text-center py-8 text-muted-foreground mt-4">
                  {labels.empty.noListings}
                  {listingsError && (
                    <div className="text-sm text-red-500 mt-2">
                      {labels.error.error}:{" "}
                      {listingsError instanceof Error
                        ? listingsError.message
                        : labels.error.unknownError}
                    </div>
                  )}
                  {allListings.length > 0 && filteredListings.length === 0 && (
                    <div className="text-sm text-muted-foreground mt-2">
                      {labels.empty.filterNoResults.replace(
                        "{count}",
                        String(allListings.length),
                      )}
                    </div>
                  )}
                </div>
              ) : filteredAndSortedListings.length > 0 ? (
                <>
                  <div
                    className={`w-full mt-4 ${
                      isNativeApp ? "px-0" : "px-[10px] sm:px-3 md:px-4"
                    }`}
                  >
                    <div
                      className={cn(
                        "grid gap-4",
                        viewMode === "grid"
                          ? isNativeApp
                            ? "grid-cols-2 gap-2.5"
                            : "grid-cols-2 md:grid-cols-4"
                          : "grid-cols-1",
                      )}
                    >
                      {listingsWithFavorites.map((listing) => (
                        <CarCard
                          key={listing.id}
                          listing={listing}
                          viewMode={viewMode}
                          onFavoriteToggle={(listingId) => {
                            const listing = listingsWithFavorites.find(
                              (l) => l.id === listingId,
                            );
                            if (listing) {
                              if (listing.isFavorite) {
                                handleRemoveFavorite(listingId);
                              } else {
                                handleAddFavorite(listingId);
                              }
                            }
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {totalListingsCount > listingsItemsPerPage && (
                    <Pagination
                      currentPage={listingsPage}
                      totalPages={totalListingsPages}
                      totalItems={totalListingsCount}
                      itemsPerPage={listingsItemsPerPage}
                      onPageChange={handleListingsPageChange}
                      onItemsPerPageChange={handleListingsItemsPerPageChange}
                      itemsPerPageOptions={[10, 25, 50]}
                    />
                  )}
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
      {isNativeAllListings && (
        <>
          {isNativeAllListings &&
            nativeListingsSearchSort.floatingSortControls(
              !nativeFilterBarVisible,
            )}
          {nativeListingsSearchSort.sheets}
        </>
      )}
      <GuestLoginPrompt
        open={guestLoginOpen}
        onOpenChange={setGuestLoginOpen}
      />
    </div>
  );
};

export default Auctions;
